import React, { useCallback, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import classNames from 'classnames';
import styled from 'styled-components';
import Switch from 'react-switch';
import Utils from '../../../shared/utils';
import ProductCover from '../../../shared/common/ProductCover';
import { PreviewButton } from '../../../shared/preview/PreviewButton';
import ProductSeries from '../../../shared/product/ProductSeries';
import ProductAuthors from '../../../shared/product/ProductAuthors';
import ProductNarrators from '../../../shared/product/ProductNarrators';
import ProductGenres from '../../../shared/product/ProductGenres';
import ProductAge from '../../../shared/product/ProductAge';
import { ProductFormat } from '../../../shared/product/ProductFormat';
import ProductPublisher from '../../../shared/product/ProductPublisher';
import { ProductReleaseDate } from '../../../shared/product/ProductReleaseDate';
import ProductUsageConditions from '../../../shared/product/ProductUsageConditions';
import NumberSpinner from '../../../shared/common/NumberSpinner';
import ProductLanguage from '../../../shared/product/ProductLanguage';
import { THEME_PRIMARY, THEME_RED, themeColor } from '../../../styles/basics/colors';
import { ListBadges } from '../../../shared/common/BadgesV2';
import { RESERVE_ACCEPT_ACTION_VALUE, RESERVE_REJECT_ACTION_VALUE } from '../constants';
import { ProductListItemProps } from '../../../shared/common/ProductList';
import MaterialIcon from '../../../shared/common/MaterialIcon';
import { Trans, useTranslation } from 'react-i18next';
import { TFunction } from 'i18next';
import FormatCurrency from '../../../shared/utils/formatCurrency';
import { ProductMetaData } from '../../../../models/Product';
import { Article } from '../../../../models/Article';
import { ProductOut } from '../../../store/api';
import { ReserveListCopyStockDropdown } from './ReserveListCopyStock';
import { useProductAcceptRejectMutation } from '../api/reserve-list-item/useProductAcceptRejectMutation';
import { ACTION_DECREASE, ACTION_INCREASE, ACTION_SET } from '../../../store/constants';
import { hasProductSelection } from '../../../shared/product-selection/ProductSelectionUtils';
import ProductAvailableDates from '../../../shared/product/ProductAvailableDates';
import { CopiesListDropdown } from '../../../shared/product/CopiesListDropdown';
import { CopiesList } from '../../../shared/product/CopiesList';
import { fetchReserveListItemCopies } from '../../../api/reserveLists/fetchReserveListItemCopies';
import { concreteLoanFormatKeyToObject, concreteLoanFormatNameToKey } from '../../../shared/loanFormat/mappers';

const StActionButtonContainer = styled.div`
    display: flex !important;
    justify-content: flex-end;
    margin-bottom: 8px !important;
`;

const StNumberSpinner = styled(NumberSpinner)<{ disabled: boolean }>`
    font-size: 16px;
    opacity: ${props => (props.disabled ? 0.3 : null)};
`;

const StSwitch = styled(Switch)`
    margin-right: 10px;
`;

const StSwitchContainer = styled.div.attrs({ 'data-displayname': 'SwitchContainer' })`
    display: flex;
    align-items: center;
    margin: 10px 0;
    justify-content: flex-end;
`;

const StExcludedLabel = styled.div`
    display: flex;
    justify-content: center;
    align-items: center;
    height: 100%;
    font-size: 12px;
    color: white;
    padding-right: 30px;
`;

const StIncludedLabel = styled.div`
    display: flex;
    justify-content: center;
    align-items: center;
    height: 100%;
    font-size: 12px;
    color: white;
    padding-left: 30px;
`;

const StReserveListStats = styled.div`
    padding-bottom: 10px;
    width: fit-content;
`;

const StActionColumn = styled.div`
    width: 200px;
`;

type Props = ProductListItemProps & {
    readonly hideAcceptReject: boolean;
    readonly reserveListId: string;
    readonly readonly: boolean;
    readonly onCopyAmountChange: (
        amount: number,
        command: typeof ACTION_SET | typeof ACTION_INCREASE | typeof ACTION_DECREASE
    ) => Promise<void>;
};

interface RedundantStatisticPopupsProps {
    product: (ProductMetaData & Article) | ProductOut;
    isInPartnerContext?: boolean;
    showReserveInfoSplit: boolean;
    t: TFunction;
}

export const RedundantStatisticPopups = ({
    product,
    isInPartnerContext,
    showReserveInfoSplit,
    t
}: RedundantStatisticPopupsProps) => {
    let foreignReserveCount: number;
    let ownReserveCount: number;

    const activeArrowStyle = { bottom: 'initial', top: '-9px', left: '43px', backgroundPositionY: 0 };
    const popupContainerStyle = {
        left: 'initial',
        bottom: 'initial',
        width: 'initial',
        marginLeft: '-45px',
        marginTop: '10px'
    };

    const combinedReserveInfo = product.combinedReserveInfo;

    if (isInPartnerContext) {
        ownReserveCount = combinedReserveInfo?.foreignReserveCount ? combinedReserveInfo.foreignReserveCount : 0;
        foreignReserveCount = 0;
    } else {
        ownReserveCount = combinedReserveInfo?.ownReserveCount ? combinedReserveInfo.ownReserveCount : 0;
        foreignReserveCount = combinedReserveInfo?.foreignReserveCount ? combinedReserveInfo.foreignReserveCount : 0;
    }

    return (
        <>
            {showReserveInfoSplit && ownReserveCount + foreignReserveCount > 0 ? (
                <div style={popupContainerStyle} className="default-dd details-dropdown reserve-amount-details">
                    <span className="active-arrow" style={activeArrowStyle} />
                    <div className="reserve-dropdown-header">
                        <strong>{t('reserveReports.reserveReport.viewReport.currentReservesQueue')}</strong>
                    </div>
                    {ownReserveCount + foreignReserveCount > 0 ? (
                        <div className="reserve-amount-info">
                            <Trans
                                i18nKey="reserveReports.reserveReport.viewReport.yourUsers"
                                components={[<strong />]}
                                values={{ ownReserveCount }}
                            />
                        </div>
                    ) : null}
                    {foreignReserveCount > 0 ? (
                        <div className="reserve-amount-info">
                            <Trans
                                i18nKey="reserveReports.reserveReport.viewReport.usersOnOtherSites"
                                values={{ foreignReserveCount }}
                                components={[<strong />]}
                            />
                        </div>
                    ) : null}
                    {product.id ? (
                        <div className="available-dates">
                            <div className="availability-info">
                                <strong>{t('components.productStatistics.availableOnDate')}</strong>
                            </div>
                            <ProductAvailableDates productId={product.id} />
                        </div>
                    ) : null}
                </div>
            ) : null}
        </>
    );
};

const ReserveListItem = ({
    hideAcceptReject,
    isInPartnerContext,
    onCopyAmountChange,
    product,
    productSelection,
    readonly,
    reserveListId
}: Props) => {
    const popoverRef = useRef<HTMLElement>(null);
    const [showDetails, setShowDetails] = useState(false);
    const [showCopyStockSplit, setShowCopyStockSplit] = useState(false);
    const [showReserveInfoSplit, setShowReserveInfoSplit] = useState(false);
    const { t } = useTranslation();

    const { mutate: mutateProductAcceptReject, isLoading: isMutating } = useProductAcceptRejectMutation(
        reserveListId,
        product.id
    );

    function calcReservePerCopy(copies: number) {
        return Math.floor((product.reserveAmount ?? 0) / copies);
    }

    function calcTargetReserves() {
        return Math.max((product.reserveAmount ?? 0) - (product.copyAmount ?? 0), 0);
    }

    function TargetInfos({ value }: { value: number }) {
        return (
            <span>
                &nbsp;
                <i className="material-icons mi-green md-up reserve-info-icon-arrow">arrow_forward</i>
                &nbsp;
                <span className="fatdata cold-bgreen">{value}</span>
            </span>
        );
    }

    const loanFormatKey = concreteLoanFormatNameToKey(product.loanFormat);

    //same value as loanFormatKey, loanFormatKey may change later
    const productLoanFormatKey = concreteLoanFormatNameToKey(product.loanFormat);
    const productLoanFormatObject = concreteLoanFormatKeyToObject(productLoanFormatKey);
    const packshotClasses = classNames('product-list-cell', 'packshot', 'catalogue', {
        over: showDetails
    });

    let productLink = '#';
    if (!hasProductSelection(productSelection)) {
        productLink = `/products/${product.id}`;
    }

    const combinedTitleLabel = product.title + (product.subTitle ? ` ${product.subTitle}` : '');

    const copyStockAmount = product.copyStockAmount ?? 0;
    const copyAmount = product.copyAmount ?? 0;
    const actionsClass = classNames('tc', 'tcw-20', 'actions', {
        disabled: !product.availableForSale
    });
    const noLongerForSaleClass = classNames('available-info', {
        notvisible: product.availableForSale
    });

    const productListRowClasses = classNames('product-list-row', { 'rejected-row': !product.accepted });
    const nextAcceptRejectValue = product.accepted ? RESERVE_REJECT_ACTION_VALUE : RESERVE_ACCEPT_ACTION_VALUE;
    const priceInfoClassNames = classNames('price-label', {
        'cold-dark-red': product.price?.basePrice,
        strong: product.price?.basePrice
    });

    const ReservesPerCopyIcon = () => (
        <i className="reserve-list-stat-icon bolinda-icons orange d-inline-block">reserves_per_copy</i>
    );
    const ReservesIcon = () => <i className="reserve-list-stat-icon material-icons small orange">person</i>;
    const ReservesPerCopy = () => (
        <span className="fatdata">
            {t('numberFormat.numberWithoutDecimalPoint', { val: calcReservePerCopy(copyStockAmount) })}
        </span>
    );
    const Reserves = () => (
        <span className="fatdata">
            {t('numberFormat.numberWithoutDecimalPoint', { val: product.reserveAmount ?? 0 })}
        </span>
    );
    const CurrentCopiesLessThanZero = () => <span className="fatdata">0</span>;
    const CurrentCopies = () => (
        <span className="fatdata">{t('numberFormat.numberWithoutDecimalPoint', { val: copyStockAmount ?? 0 })}</span>
    );

    const handleCopyAmountChange = useCallback(
        (command: typeof ACTION_SET | typeof ACTION_INCREASE | typeof ACTION_DECREASE, amount: number) =>
            onCopyAmountChange(amount, command),
        [onCopyAmountChange]
    );
    return (
        <div className={productListRowClasses} data-displayname="ReserveListItem">
            <div className={packshotClasses}>
                <div className={`${!product.accepted ? 'rejected-item-opacity' : null}`}>
                    <Link to={productLink}>
                        <ProductCover
                            onMouseEnter={() => setShowDetails(true)}
                            onMouseLeave={() => setShowDetails(false)}
                            minSizeInPx={288}
                            product={product}
                        />
                    </Link>
                    <PreviewButton product={product} />
                </div>
            </div>
            <div className="product-list-cell">
                <div className="product-list-details">
                    <div style={{ display: 'table-row' }}>
                        <div className={`tc tcw-25 ${!product.accepted ? 'rejected-item-opacity' : null}`}>
                            <ProductSeries product={product} loanFormatKey={loanFormatKey} shopLanguage={undefined} />
                            <p title={combinedTitleLabel} className="label">
                                <Link to={productLink}>
                                    <strong>{Utils.truncate(combinedTitleLabel)}</strong>
                                </Link>
                            </p>
                            <ProductAuthors product={product} loanFormatKey={loanFormatKey} shopLanguage={undefined} />
                            <ProductNarrators
                                product={product}
                                loanFormatKey={loanFormatKey}
                                shopLanguage={undefined}
                            />
                        </div>
                        <div className={`tc tcw-25 ${!product.accepted ? 'rejected-item-opacity' : null}`}>
                            <ProductFormat product={product} loanFormatKey={loanFormatKey} shopLanguage={undefined} />
                            <ProductGenres product={product} loanFormatKey={loanFormatKey} shopLanguage={undefined} />
                            <ProductAge product={product} loanFormatKey={loanFormatKey} shopLanguage={undefined} />
                            <ProductLanguage product={product} loanFormatKey={loanFormatKey} shopLanguage={undefined} />
                        </div>
                        <div className={`tc tcw-25 ${!product.accepted ? 'rejected-item-opacity' : null}`}>
                            <p>{product.isbn13}</p>
                            <ProductReleaseDate product={product} />
                            <ProductPublisher
                                product={product}
                                loanFormatKey={loanFormatKey}
                                shopLanguage={undefined}
                            />
                            <ProductUsageConditions
                                product={product}
                                loanFormatKey={loanFormatKey}
                                shopLanguage={undefined}
                            />
                        </div>
                        <div className="tc tcw-25" data-displayname="ReserveStatistics">
                            <StReserveListStats>
                                <Trans
                                    i18nKey="reserveReports.reserveReport.viewReport.reservesPerCopy"
                                    components={{
                                        1: <ReservesPerCopyIcon />,
                                        2: <ReservesPerCopy />
                                    }}
                                />
                                {product.accepted ? (
                                    <TargetInfos value={calcReservePerCopy(copyStockAmount + copyAmount)} />
                                ) : null}
                            </StReserveListStats>

                            <StReserveListStats
                                onMouseEnter={() => setShowReserveInfoSplit(true)}
                                onMouseLeave={() => setShowReserveInfoSplit(false)}
                            >
                                <Trans
                                    i18nKey="reserveReports.reserveReport.viewReport.reserves"
                                    components={{
                                        1: <ReservesIcon />,
                                        2: <Reserves />
                                    }}
                                />
                                {product.accepted ? <TargetInfos value={calcTargetReserves()} /> : null}
                                <RedundantStatisticPopups
                                    product={product}
                                    isInPartnerContext={isInPartnerContext}
                                    showReserveInfoSplit={showReserveInfoSplit}
                                    t={t}
                                />
                            </StReserveListStats>

                            {copyStockAmount <= 0 ? (
                                <StReserveListStats>
                                    <span>
                                        <Trans
                                            i18nKey="reserveReports.reserveReport.viewReport.currentCopies"
                                            components={{
                                                1: (
                                                    <MaterialIcon
                                                        icon="content_copy"
                                                        className="reserve-list-stat-icon small orange"
                                                    />
                                                ),
                                                2: <CurrentCopiesLessThanZero />
                                            }}
                                        />
                                        {product.accepted ? <TargetInfos value={copyStockAmount + copyAmount} /> : null}
                                    </span>
                                </StReserveListStats>
                            ) : (
                                <StReserveListStats
                                    onMouseEnter={() => setShowCopyStockSplit(true)}
                                    onMouseLeave={() => setShowCopyStockSplit(false)}
                                >
                                    <span ref={popoverRef}></span>
                                    <Trans
                                        i18nKey="reserveReports.reserveReport.viewReport.currentCopies"
                                        components={{
                                            1: (
                                                <MaterialIcon
                                                    icon="content_copy"
                                                    className="reserve-list-stat-icon small orange"
                                                />
                                            ),
                                            2: <CurrentCopies />
                                        }}
                                    />

                                    {product.accepted ? <TargetInfos value={copyStockAmount + copyAmount} /> : null}
                                    {product.ownStockQuantity || product.foreignStockQuantity ? (
                                        <ReserveListCopyStockDropdown
                                            popoverRef={popoverRef}
                                            visible={showCopyStockSplit}
                                            loanFormat={productLoanFormatObject!}
                                            placement="bottom-start"
                                            ownStockQuantity={product.ownStockQuantity}
                                            foreignStockQuantity={product.foreignStockQuantity}
                                            isInPartnerContext={isInPartnerContext}
                                        />
                                    ) : null}
                                </StReserveListStats>
                            )}
                        </div>
                        {product.availableForSale ? (
                            <StActionColumn className={actionsClass}>
                                <StActionButtonContainer className="bt-line">
                                    <CopiesListDropdown>
                                        <CopiesList
                                            disabled={!(product as any).accepted}
                                            removeCopyHint={t('components.productDetails.removeHint.reserveList')}
                                            product={product}
                                            onRemove={() => onCopyAmountChange(1, ACTION_DECREASE)}
                                            fetchFunc={productId =>
                                                fetchReserveListItemCopies(reserveListId, productId)
                                            }
                                            minCopyAmount={1}
                                        />
                                    </CopiesListDropdown>

                                    <StNumberSpinner
                                        product={product}
                                        value={product.availableForSale ? copyAmount : 0}
                                        min={0}
                                        disabled={!product.accepted || readonly}
                                        onChange={handleCopyAmountChange}
                                    />
                                </StActionButtonContainer>
                                <StSwitchContainer>
                                    <StSwitch
                                        disabled={hideAcceptReject || isMutating}
                                        checked={product.accepted || false}
                                        onChange={() => mutateProductAcceptReject({ action: nextAcceptRejectValue })}
                                        offColor={themeColor(THEME_RED)}
                                        onColor={themeColor(THEME_PRIMARY)}
                                        width={85}
                                        height={24}
                                        handleDiameter={20}
                                        uncheckedIcon={
                                            <StExcludedLabel>
                                                {t('reserveReports.reserveReport.viewReport.excluded')}
                                            </StExcludedLabel>
                                        }
                                        checkedIcon={
                                            <StIncludedLabel>
                                                {t('reserveReports.reserveReport.viewReport.included')}
                                            </StIncludedLabel>
                                        }
                                    />
                                    <span className={priceInfoClassNames}>
                                        {t('numberFormat.numberWithCurrencySymbol', {
                                            val: FormatCurrency.convertIntegerToCurrencyFormat(product.price?.netValue),
                                            currencySymbol: FormatCurrency.mapCurrencyToSymbol(product.price?.currency)
                                        })}
                                    </span>
                                </StSwitchContainer>
                                {!hideAcceptReject && product.price?.basePrice ? (
                                    <span className="discount">
                                        {t('reserveReports.reserveReport.viewReport.save', {
                                            amount: t('numberFormat.numberWithCurrencySymbol', {
                                                val: FormatCurrency.convertIntegerToCurrencyFormat(
                                                    product.price?.savedValue
                                                ),
                                                currencySymbol: FormatCurrency.mapCurrencyToSymbol(
                                                    product.price?.currency
                                                )
                                            }),
                                            percentile: Math.round((product.price?.savedPercentage ?? 0) / 100)
                                        })}
                                        <span className="fl-right crossed">
                                            {t('numberFormat.numberWithCurrencySymbol', {
                                                val: FormatCurrency.convertIntegerToCurrencyFormat(
                                                    product.price?.basePrice
                                                ),
                                                currencySymbol: FormatCurrency.mapCurrencyToSymbol(
                                                    product.price?.currency
                                                )
                                            })}
                                        </span>
                                    </span>
                                ) : null}
                            </StActionColumn>
                        ) : (
                            <div className={actionsClass}>
                                <span className={noLongerForSaleClass}>
                                    {t('reserveReports.reserveReport.viewReport.noLongerForSale')}
                                </span>
                            </div>
                        )}
                    </div>
                </div>
                <div className={`product-list-bottom ${!product.accepted ? 'rejected-item-opacity' : null}`}>
                    <ListBadges
                        product={product}
                        loanFormatKey={loanFormatKey}
                        showAll={showDetails}
                        shopLanguage={undefined}
                    />
                </div>
            </div>
        </div>
    );
};

export default ReserveListItem;

ReserveListItem.defaultProps = {
    product: {},
    onCopyAmountChange: () => {},
    onAddToList: () => {},
    onSelectProduct: () => {}
};
